import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Bell, BellRing, Check, CheckCheck, UserPlus, GraduationCap, 
  AlertCircle, User, Clock, ChevronRight, Shield, Calendar, 
  Activity, Wifi, WifiOff
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function NotificationBell() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);

  const isAdmin = user?.role === "admin" || user?.role === "administrator";

  // Handle incoming real-time notification
  const handleRealtimeNotification = useCallback((notification) => {
    setNotifications(prev => [notification, ...prev].slice(0, 50));
    setUnreadCount(prev => prev + 1);
    
    // Show toast for important notifications
    const showToast = (type, title, message, priority) => {
      if (priority === "urgent" || priority === "high") {
        toast.error(title, { description: message, duration: 8000 });
      } else if (type === "compliance_alert" || type === "eligibility_alert") {
        toast.warning(title, { description: message, duration: 5000 });
      } else {
        toast.info(title, { description: message, duration: 4000 });
      }
    };
    
    showToast(notification.type, notification.title, notification.message, notification.priority);
  }, []);

  // WebSocket connection for real-time notifications
  useEffect(() => {
    if (!user?.id) return;
    
    let ws;
    let reconnectTimeout;
    
    const connect = () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL || "";
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsHost = API_URL.replace(/^https?:\/\//, '').replace(/\/api$/, '');
        const token = localStorage.getItem('hospice_token');
        
        if (!wsHost || !token) return;
        
        const wsUrl = `${wsProtocol}//${wsHost}/api/realtime/ws/notifications?token=${token}`;
        ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          setWsConnected(true);
          ws.send(JSON.stringify({
            type: 'subscribe',
            channels: ['compliance_alerts', 'timeline_events', 'eligibility_alerts', 'visit_updates']
          }));
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'notification' && data.notification) {
              handleRealtimeNotification(data.notification);
            }
          } catch (e) {
            console.warn('Failed to parse WS message:', e);
          }
        };
        
        ws.onclose = () => {
          setWsConnected(false);
          reconnectTimeout = setTimeout(connect, 5000);
        };
        
        ws.onerror = () => setWsConnected(false);
        
      } catch (error) {
        console.warn('WebSocket connection failed:', error);
      }
    };
    
    connect();
    
    return () => {
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (ws) ws.close();
    };
  }, [user?.id, handleRealtimeNotification]);

  useEffect(() => {
    const loadNotifications = async () => {
      try {
        const res = await api.get("/notifications?limit=20");
        setNotifications(res.data.notifications || []);
        setUnreadCount(res.data.unread_count || 0);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    if (user) {
      loadNotifications();
      // Poll every 30 seconds
      const interval = setInterval(loadNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const markAsRead = async (notificationId) => {
    try {
      await api.patch(`/notifications/${notificationId}`, { read: true });
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "partner_registration":
        return <UserPlus className="w-4 h-4 text-blue-500" />;
      case "overdue_training":
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case "patient_assigned":
        return <User className="w-4 h-4 text-green-500" />;
      case "account_approved":
        return <Check className="w-4 h-4 text-green-500" />;
      case "new_referral":
        return <UserPlus className="w-4 h-4 text-purple-500" />;
      case "compliance_alert":
        return <Shield className="w-4 h-4 text-amber-500" />;
      case "timeline_event":
        return <Calendar className="w-4 h-4 text-blue-500" />;
      case "eligibility_alert":
        return <Activity className="w-4 h-4 text-red-500" />;
      case "visit_update":
        return <Check className="w-4 h-4 text-emerald-500" />;
      case "auto_fill_completed":
        return <Check className="w-4 h-4 text-emerald-500" />;
      case "documentation_gap":
        return <AlertCircle className="w-4 h-4 text-amber-500" />;
      case "patient_symptom":
        return <Activity className="w-4 h-4 text-rose-500" />;
      case "family_message":
        return <User className="w-4 h-4 text-indigo-500" />;
      case "visit_reminder":
        return <Clock className="w-4 h-4 text-blue-500" />;
      case "medication_alert":
        return <AlertCircle className="w-4 h-4 text-orange-500" />;
      case "care_plan_update":
        return <Shield className="w-4 h-4 text-teal-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent": return "bg-red-100 text-red-700 border-red-200";
      case "high": return "bg-amber-100 text-amber-700 border-amber-200";
      case "medium": return "bg-blue-100 text-blue-700 border-blue-200";
      default: return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="relative" data-testid="notification-bell">
          {unreadCount > 0 ? (
            <BellRing className="w-5 h-5 text-amber-600" />
          ) : (
            <Bell className="w-5 h-5 text-muted-foreground" />
          )}
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500 text-white"
              data-testid="notification-count"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
          {/* WebSocket connection indicator */}
          <span className={`absolute bottom-0 right-0 w-2 h-2 rounded-full ${
            wsConnected ? 'bg-emerald-500' : 'bg-slate-400'
          }`} title={wsConnected ? 'Real-time connected' : 'Polling mode'} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Notifications
          </span>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-xs h-6"
              onClick={markAllAsRead}
            >
              <CheckCheck className="w-3 h-3 mr-1" />
              Mark all read
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <ScrollArea className="h-80">
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No notifications</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={`p-3 cursor-pointer ${!notification.read ? 'bg-blue-50/50' : ''}`}
                onClick={() => {
                  if (!notification.read) markAsRead(notification.id);
                  if (notification.link) {
                    setOpen(false);
                  }
                }}
              >
                {notification.link ? (
                  <Link to={notification.link} className="flex gap-3 w-full">
                    <NotificationContent notification={notification} getIcon={getNotificationIcon} getPriorityColor={getPriorityColor} formatTime={formatTime} />
                  </Link>
                ) : (
                  <div className="flex gap-3 w-full">
                    <NotificationContent notification={notification} getIcon={getNotificationIcon} getPriorityColor={getPriorityColor} formatTime={formatTime} />
                  </div>
                )}
              </DropdownMenuItem>
            ))
          )}
        </ScrollArea>
        {notifications.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className="p-2">
              <Link to="/notifications">
                <Button variant="ghost" size="sm" className="w-full text-xs">
                  View all notifications
                  <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NotificationContent({ notification, getIcon, getPriorityColor, formatTime }) {
  return (
    <>
      <div className="flex-shrink-0 mt-1">
        {getIcon(notification.type)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm font-medium truncate ${!notification.read ? 'text-foreground' : 'text-muted-foreground'}`}>
            {notification.title}
          </p>
          {!notification.read && (
            <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-1.5" />
          )}
        </div>
        <p className="text-xs text-muted-foreground line-clamp-2">
          {notification.message}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatTime(notification.created_at)}
          </span>
          {notification.priority && notification.priority !== "normal" && (
            <Badge variant="outline" className={`text-xs ${getPriorityColor(notification.priority)}`}>
              {notification.priority}
            </Badge>
          )}
        </div>
      </div>
    </>
  );
}
